import * as moment from 'moment-timezone';

// function to convert to different timezone from local should calculate timezone offset and add the offset to utc date
export function convertToTz(date: string | Date, tzString: string) {
  return new Date(
    (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', {
      timeZone: tzString,
    }),
  );
}

export function convertTozKeepTime(
  date: string | number | Date,
  zone: string,
): string {
  return (!date ? moment() : moment(date).tz(zone, true)).format();
}
export function convertToUtc(date?: string | number | Date): Date {
  return (!date ? moment().utc() : moment(date).utc()).toDate();
}

export function currentUtcTime(): Date {
  return moment().utc().toDate();
}

export function today(): Date {
  return moment().startOf('day').toDate();
}

export function StartOfDay(): Date {
  return moment().startOf('day').toDate();
}
export function StartOfWeek(): Date {
  return moment().startOf('week').toDate();
}
export function StartOfMonth(): Date {
  return moment().startOf('month').toDate();
}
export function StartOfYear(): Date {
  return moment().startOf('year').toDate();
}

export function updateGlobalLocale(
  localeName: string,
  customeConfig: { [key: string]: any },
) {
  moment.updateLocale(localeName, customeConfig);
}

export function createDate(date?: string | number | Date): Date {
  return (!date ? moment() : moment(date)).toDate();
}

export function isValidDate(date: string | number | Date): boolean {
  return moment(date).isValid();
}

export function diffDate(
  a: string | number | Date,
  b: string | number | Date,
  unit?: moment.unitOfTime.Diff,
): number {
  return moment(a).diff(b, unit);
}

export function fromNow(date: string | number | Date): string {
  return moment(date).utc().from(moment(new Date()).utc(), true);
}

export function toNow(date: string | number | Date): string {
  return moment(date).utc().to(moment(new Date()).utc(), true);
}

export function isAfterDate(
  source: string | number | Date,
  target: string | number | Date,
): boolean {
  return moment(source).isAfter(target);
}

export function isBeforeDate(
  source: string | number | Date,
  target: string | number | Date,
): boolean {
  return moment(source).isBefore(target);
}

export function isSameOrAfterDate(
  source: string | number | Date,
  target: string | number | Date,
): boolean {
  const date = moment(source);
  return date.isSame(target) || date.isAfter(target);
}

export function isSameOrBeforeDate(
  source: string | number | Date,
  target: string | number | Date,
): boolean {
  const date = moment(source);
  return date.isSame(target) || date.isBefore(target);
}

export function getCurrentWeekRange(): [Date, Date] {
  const date = moment();
  return [
    convertToUTCWithoutChangingTime(date.startOf('week')),
    convertToUTCWithoutChangingTime(date.endOf('week')),
  ];
}

export function getCurrentMonthRange(): [Date, Date] {
  const date = moment();
  return [
    convertToUTCWithoutChangingTime(date.startOf('month')),
    convertToUTCWithoutChangingTime(date.endOf('month')),
  ];
}

export function getCurrentYearRange(): [Date, Date] {
  const date = moment();
  return [
    convertToUTCWithoutChangingTime(date.startOf('year')),
    convertToUTCWithoutChangingTime(date.endOf('year')),
  ];
}

export function convertToUTCWithoutChangingTime(
  date: Date | number | string | moment.Moment,
): Date {
  return moment(date).utc(true).toDate();
}

export function getTimeZoneOffset(dateTimeZone: string): string {
  const date = new Date();
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(
    date.toLocaleString('en-US', { timeZone: dateTimeZone }),
  );
  const offset = (tzDate.getTime() - utcDate.getTime()) / 60000; // offset in minutes
  const hours = Math.floor(offset / 60);
  const minutes = offset % 60;
  const sign = offset >= 0 ? '+' : '-';
  return `${sign}${padZero(Math.abs(hours))}:${padZero(Math.abs(minutes))}`;
}
function padZero(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}
export function formatValue(
  value: string,
  dateFormat: string,
  dateTimeFormat: string,
  timeZone: string,
) {
  const valueType = getValueType(value);
  switch (valueType) {
    case 'date':
      return moment(value).format(dateFormat);
    case 'datetime':
      return moment(value)
        .utcOffset(getTimeZoneOffset(timeZone))
        .format(dateTimeFormat);
    case 'time':
      return value; // TODO: format time
    case 'string':
      return value;
  }
}

export function formatDateTime(
  value: string,
  dateTimeFormat: string,
  timeZone: string,
) {
  return moment(value)
    .utcOffset(getTimeZoneOffset(timeZone))
    .format(dateTimeFormat);
}
export function getValueType(
  value: string,
): 'date' | 'datetime' | 'time' | 'string' {
  // Regular expressions for common date, datetime, and time formats
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD
  const dateTimeRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/; // ISO 8601 format
  const timeRegex = /^\d{2}:\d{2}(:\d{2})?$/; // HH:mm or HH:mm:ss

  if (dateRegex.test(value)) {
    // If the input matches the date format
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return 'date';
    }
  } else if (dateTimeRegex.test(value)) {
    // If the input matches the datetime format
    const dateTime = new Date(value);
    if (!isNaN(dateTime.getTime())) {
      return 'datetime';
    }
  } else if (timeRegex.test(value)) {
    // If the input matches the time format
    return 'time';
  }

  // If none of the above checks passed, it's just data (not a date, datetime, or time)
  return 'string';
}
